import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import Button from 'shopper/components/Button';
import Heading from 'shopper/components/Heading';

import OfferCard from 'components/Timeline/OfferCard/V2/OfferGridCard';

import useInfiniteQuery from 'hooks/useInfiniteQuery';

import placeholder from 'lib/placeholder';
import { offerQueryKeys } from 'lib/queryKeys';
import { fromTextToDomId } from 'lib/string';

import { getSelectionOffersList } from 'services/selection';

const OfferCardSkeleton = dynamic(
  () => import('components/Timeline/OfferCard/V2/OfferGridCard/Skeleton'),
  { ssr: false }
);
const SkeletonGroup = dynamic(
  () => import('components/Skeleton/SkeletonGroup'),
  { ssr: false }
);

const OFFERS_PAGINATION_LIMIT = 8;
const OFFERS_SKELETON_ITEMS = 3;

const ManagedOffersList = ({
  gaEventCategory,
  initialOffers,
  timelineId,
  title,
}) => {
  const {
    data: offers,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isSuccess,
    hasNextPage,
    isInitialLoading,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: offerQueryKeys.listBySelection({ selectionSlug: timelineId }),
    queryFn: (after, { signal }) =>
      getSelectionOffersList(
        { slug: timelineId, after, limit: OFFERS_PAGINATION_LIMIT },
        { signal }
      ),
    getNextPageParam: (lastOffers) => lastOffers?.after,
    flattenKey: 'offers',
    initialData: initialOffers || undefined,
    refetchOnMount: true,
    staleTime: 0,
    cacheTime: 0.5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
  });

  const onLoadMoreOffers = () => {
    fetchNextPage();
  };

  const canLoadMoreOffers = !isLoading && hasNextPage;
  const hasLoading = !!(
    isFetchingNextPage ||
    (isFetching && !isInitialLoading)
  );

  if (isSuccess && offers.length === 0) {
    return null;
  }

  return (
    <section id={fromTextToDomId(title)}>
      <Heading as="h2" className="mb-4 lg:mb-6" size="size3">
        {title}
      </Heading>
      <div className="grid grid-flow-row grid-cols-1 gap-4 lg:grid-cols-4">
        {isSuccess &&
          offers.map((offer, index) => (
            <OfferCard
              key={`${offer.key}-${index}`}
              gaEventCategory={gaEventCategory}
              {...offer}
            />
          ))}
        {hasLoading && (
          <SkeletonGroup
            items={OFFERS_SKELETON_ITEMS}
            skeleton={OfferCardSkeleton}
          />
        )}
      </div>
      {canLoadMoreOffers && (
        <Button
          className="mx-auto mt-6"
          size="size2"
          type="primary"
          onClick={onLoadMoreOffers}
        >
          {placeholder('actions.seeMore')}
        </Button>
      )}
    </section>
  );
};

ManagedOffersList.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  initialOffers: PropTypes.shape(),
  timelineId: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
};

export default ManagedOffersList;
